/**
 * CL-CART-01 — CartPage data loading.
 *
 * Fetches every listing in the cart together with its currentStock, so the page recomputes
 * from live data. The cart's stored snapshots are for painting a row before the listing
 * arrives, and for nothing else.
 */

import { createSlice } from '@reduxjs/toolkit';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { selectCart } from '../../ducks/cart.duck';

// Sharetribe's maximum perPage for listings.query.
const LISTINGS_PER_QUERY = 100;

const initialState = {
  fetchInProgress: false,
  fetchError: null,
  listings: [],
  // Whether the listings below are the answer to the current cart, or just what we happen to
  // have. Until a fetch SUCCEEDS, a row with no listing means "not read yet" rather than
  // "gone" — see PENDING in cartAvailability.js. A failed fetch puts it back to false, so a
  // network blip cannot make the page declare a cart full of deleted items.
  listingsResolved: false,
};

const cartPageSlice = createSlice({
  name: 'CartPage',
  initialState,
  reducers: {
    fetchListingsRequest: state => {
      state.fetchInProgress = true;
      state.fetchError = null;
    },
    fetchListingsSuccess: (state, action) => {
      state.fetchInProgress = false;
      state.listings = action.payload;
      state.listingsResolved = true;
    },
    fetchListingsError: (state, action) => {
      state.fetchInProgress = false;
      state.fetchError = action.payload;
      state.listingsResolved = false;
    },
  },
});

export const {
  fetchListingsRequest,
  fetchListingsSuccess,
  fetchListingsError,
} = cartPageSlice.actions;

export default cartPageSlice.reducer;

export const selectCartListings = state => state.CartPage.listings;
export const selectFetchInProgress = state => state.CartPage.fetchInProgress;
export const selectListingsResolved = state => state.CartPage.listingsResolved;
export const selectFetchError = state => state.CartPage.fetchError;

export const fetchCartListings = () => (dispatch, getState, sdk) => {
  const cart = selectCart(getState());
  const ids = Object.values(cart?.sellers || {}).flatMap(g => g.items.map(i => i.listingId));

  if (ids.length === 0) {
    dispatch(fetchListingsSuccess([]));
    return Promise.resolve([]);
  }

  dispatch(fetchListingsRequest());

  // Chunked, because the cap is 25 items PER SELLER with no limit on sellers: five sellers
  // is 125 ids against a default page size of 100. Without this the ids past the first page
  // come back missing, and a missing listing renders as "No longer available" — the cart
  // would quietly tell a buyer their items were gone.
  const chunks = [];
  for (let i = 0; i < ids.length; i += LISTINGS_PER_QUERY) {
    chunks.push(ids.slice(i, i + LISTINGS_PER_QUERY));
  }

  // `sdk.listings.query`, not ownListings: these are other people's listings. currentStock
  // is what makes the availability pass mean anything — without it every row would be
  // judged on a snapshot.
  return Promise.all(
    chunks.map(chunk =>
      sdk.listings.query({
        ids: chunk,
        perPage: LISTINGS_PER_QUERY,
        include: ['author', 'images', 'currentStock'],
        'fields.image': ['variants.listing-card', 'variants.listing-card-2x'],
        // `listing-card` is a NAMED CROP this app defines per request, not a built-in
        // Sharetribe variant (see ListingPage.duck.js). Without also defining it here, the
        // image comes back with no `attributes` at all — confirmed against a real Dev
        // response. Harmless today only because CartItemRow shows `imageSnapshotUrl`,
        // captured once at add-to-cart time via the listing page's own correctly-configured
        // fetch, never this live one — found while wiring the SAME query shape into
        // CartCheckoutPage.duck.js, whose OrderItemList genuinely needed it.
        ...createImageVariantConfig('listing-card', 400, 0.75),
        ...createImageVariantConfig('listing-card-2x', 800, 0.75),
      })
    )
  )
    .then(responses => {
      const listings = responses.flatMap(response => denormalisedResponseEntities(response));
      dispatch(fetchListingsSuccess(listings));
      return listings;
    })
    .catch(e => {
      dispatch(fetchListingsError(storableError(e)));
    });
};

/**
 * Nothing to load on the server: the cart lives in browser storage, so the server does not
 * know what is in it. The listings are fetched on the client once the cart duck has
 * initialised — see CartPage.js.
 */
export const loadData = () => () => Promise.resolve();
